/**
 * Geometry helpers for the angle / sizes snap modifiers shared by
 * ShapeTool, CutoutTool, StitchTool, and ConstructionTool. Triggered
 * when the user holds Shift during a drag, or when the matching
 * snap toggle is on. The 4-button snap group (issue #98) splits
 * what used to be a single `snapToAxis` switch into independent
 * angle / point / point-constraint / sizes flags, all gated by the
 * master flag.
 */

import type { ToolSettings } from "./EditorContext";

export type Point2 = { x: number; y: number };

/** True when angle snapping (`snapToCommonAngle`) should fire: either
 *  an angle-modifier key is held (see {@link angleModifierHeld}), or both
 *  the master and the angle child toggles are on. */
export function isAngleSnapActive(
  settings: ToolSettings,
  modifierKey: boolean,
): boolean {
  return modifierKey || (settings.snapMaster && settings.snapAngle);
}

/** The keyboard modifiers that force angle snap while held: Shift (the
 *  original) or Ctrl / Cmd (#927 — a dedicated angle key, so angle can be
 *  forced without Shift's size-link side effect in the draw tools). Pass the
 *  result as `isAngleSnapActive`'s modifier argument. */
export function angleModifierHeld(s: {
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
}): boolean {
  return s.shiftKey || s.ctrlKey || s.metaKey;
}

/** True when the square / circle aspect-ratio constraint
 *  (`constrainSquare`) should fire on rect / ellipse drags. */
export function isSizesSnapActive(
  settings: ToolSettings,
  shiftKey: boolean,
): boolean {
  return shiftKey || (settings.snapMaster && settings.snapSizes);
}

/** True when a rect / ellipse drag (or a selection resize) should grow
 *  symmetrically about its start point — the "draw / resize from centre"
 *  modifier. Either the user is holding Alt / Option, or both the master
 *  and the centre child toggles are on. Mirrors the Shift override on
 *  `isSizesSnapActive`, but on the Alt key (issue #389) so keyboard-less
 *  devices can pin it via the toggle. */
export function isCenterSnapActive(
  settings: ToolSettings,
  altKey: boolean,
): boolean {
  return altKey || (settings.snapMaster && settings.snapCenter);
}

/** True when the point-snap pipeline (vertex / centre / midpoint
 *  candidates) should run. Pure setting check — Shift doesn't
 *  override this one. */
export function isPointSnapActive(settings: ToolSettings): boolean {
  return settings.snapMaster && settings.snapPoint;
}

/** True when a successful point-snap should persist as a coincident
 *  constraint on release. Requires `isPointSnapActive` to be true as
 *  well (you can't pin what didn't snap). */
export function isPointConstraintActive(settings: ToolSettings): boolean {
  return (
    settings.snapMaster && settings.snapPoint && settings.snapPointConstraint
  );
}

/** True when the drag-time grid-snap should nudge the shape's handle
 *  point to the nearest cutting-mat grid intersection. Pure setting
 *  check — Shift doesn't override this one. */
export function isGridSnapActive(settings: ToolSettings): boolean {
  return settings.snapMaster && settings.snapGrid;
}

/** True when a drawn vertex should snap to the nearest point *along* any
 *  existing construction line (issue #323). Pure setting check. */
export function isLineSnapActive(settings: ToolSettings): boolean {
  return settings.snapMaster && settings.snapLine;
}

/** Snap `pt` to the nearest intersection of the cutting-mat grid
 *  defined by `grid` (returned by `Scene.getMatGridInfo()`). The grid
 *  is laid out from `innerLeft` rightwards and `innerBottom` upwards
 *  in y-down project space, so we round against those origins to
 *  pick the closest lattice point. */
export function snapPointToGrid(
  pt: Point2,
  grid: { innerLeft: number; innerBottom: number; step: number },
): Point2 {
  return {
    x:
      grid.innerLeft +
      Math.round((pt.x - grid.innerLeft) / grid.step) * grid.step,
    y:
      grid.innerBottom -
      Math.round((grid.innerBottom - pt.y) / grid.step) * grid.step,
  };
}

/** When angle-snap AND grid-snap are both on, snap the drag endpoint along
 *  its angle ray to the nearest point where the ray CROSSES a grid line —
 *  so the user moves in a locked direction and lands on a grid position
 *  (#725). Keeps the direction (`start`→`end`) fixed and only slides the
 *  distance along it. Considers the nearest vertical- and horizontal-line
 *  crossings and picks whichever is closer to `end` along the ray, but only
 *  when it's within a quarter-step (matching `snapOffset`'s pull); returns
 *  null otherwise so the drag stays free between crossings. */
export function snapAlongRayToGrid(
  start: Point2,
  end: Point2,
  grid: { innerLeft: number; innerBottom: number; step: number },
): Point2 | null {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return null;
  const ux = dx / len;
  const uy = dy / len;
  const threshold = grid.step * 0.25;
  // Candidate distances (t) along the ray where it meets a grid line.
  let bestT: number | null = null;
  let bestDiff = Infinity;
  const consider = (t: number) => {
    const diff = Math.abs(t - len);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestT = t;
    }
  };
  if (Math.abs(ux) > 1e-6) {
    // Nearest vertical grid line to the endpoint's x.
    const nx =
      grid.innerLeft +
      Math.round((end.x - grid.innerLeft) / grid.step) * grid.step;
    consider((nx - start.x) / ux);
  }
  if (Math.abs(uy) > 1e-6) {
    // Nearest horizontal grid line (lines step upward from innerBottom).
    const ny =
      grid.innerBottom -
      Math.round((grid.innerBottom - end.y) / grid.step) * grid.step;
    consider((ny - start.y) / uy);
  }
  if (bestT === null || bestDiff > threshold) return null;
  return { x: start.x + ux * bestT, y: start.y + uy * bestT };
}

/** Pick the snap-able point on a shape closest to `touch` — the
 *  position the user's pointer landed on at drag start. Candidates
 *  are every anchor plus the bbox centre and the 4 bbox-edge
 *  midpoints when present (primitive lines only). Returns `null`
 *  only when no candidates exist (e.g. a hand-drawn polyline with no
 *  segments, which shouldn't actually happen).
 *
 *  Used by the Grid snap (issue #98 follow-up) so a drag nudges the
 *  point the user "grabbed" onto the grid, rather than an arbitrary
 *  anchor that may sit far from the touch site. */
export function pickHandlePoint(
  touch: Point2,
  shape: {
    anchors: ReadonlyArray<Point2>;
    center?: Point2 | null;
    midpoints?: ReadonlyArray<Point2>;
  },
): Point2 | null {
  const candidates: Point2[] = [...shape.anchors];
  if (shape.center) candidates.push(shape.center);
  if (shape.midpoints) candidates.push(...shape.midpoints);
  if (candidates.length === 0) return null;
  let best: Point2 = candidates[0]!;
  let bestDist = Infinity;
  for (const c of candidates) {
    const d = (c.x - touch.x) ** 2 + (c.y - touch.y) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return best;
}

/** Common drafting angles — the union of 30° and 45° multiples around the
 *  full circle (0, 30, 45, 60, 90, 120, 135, 150, 180, 210, 225, 240, 270,
 *  300, 315, 330). Pre-computed in radians so the per-snap call only does
 *  arithmetic on a fixed-size table. */
const SNAP_ANGLES_RAD: number[] = [
  0, 30, 45, 60, 90, 120, 135, 150, 180, 210, 225, 240, 270, 300, 315, 330,
].map((d) => (d * Math.PI) / 180);

export interface AngleSnapResult {
  x: number;
  y: number;
  angleRad: number;
  /** The reference direction the winning angle was measured from (code-space
   *  radians), or null when it came off the absolute horizontal table. Lets
   *  the caller draw the reference as a ghost arm on the guide ray (#162). */
  refRad: number | null;
}

/**
 * How far off a reference-relative angle the cursor may be and still be
 * captured by it: half the smallest gap in the absolute table (15°).
 *
 * The reference lattice therefore reaches exactly as far as the absolute one
 * and no further. Without this cap a reference would let the snap fire from
 * anywhere — the direction would still land on a clean angle, but not one the
 * maker aimed at, which reads as angle snap having stopped working.
 */
export const ANGLE_REFERENCE_TOLERANCE_RAD = (7.5 * Math.PI) / 180;

/** Circular distance between two angles, in `[0, π]`. */
function angleDistance(a: number, b: number): number {
  const TAU = 2 * Math.PI;
  const raw = Math.abs(a - b);
  return Math.min(raw, TAU - raw);
}

/**
 * Snap an end point to the nearest "common" drafting angle from a fixed
 * start (multiples of 30° or 45°), preserving the cursor's distance from
 * start. Used when the drag has a single directional axis (slots, polygon
 * segments, stitch lines).
 *
 * With `refRad` (#162) the same table is ALSO offered rotated onto that
 * reference direction, so a segment can be drawn parallel, perpendicular or at
 * 45° to an existing line the way it can to the page. The absolute table keeps
 * precedence: a reference candidate has to be strictly nearer the cursor AND
 * inside {@link ANGLE_REFERENCE_TOLERANCE_RAD} to win, so passing a reference
 * can never move a direction the caller would otherwise have snapped square.
 */
export function snapToCommonAngle(
  start: Point2,
  end: Point2,
  refRad?: number | null,
): AngleSnapResult {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  // Degenerate distance — keep the original endpoint and report 0 rad. The
  // caller will overwrite the hint on the next non-degenerate move.
  if (length < 1e-4) return { x: end.x, y: end.y, angleRad: 0, refRad: null };
  // Normalize the cursor angle to [0, 2π) so the nearest-neighbour scan
  // wraps cleanly across the 0/360 boundary without per-candidate modular
  // arithmetic.
  const TAU = 2 * Math.PI;
  let angle = Math.atan2(dy, dx) % TAU;
  if (angle < 0) angle += TAU;
  let best = SNAP_ANGLES_RAD[0]!;
  let bestDiff = Infinity;
  for (const cand of SNAP_ANGLES_RAD) {
    // Circular distance — pick the smaller of "going forward" and "going
    // around the other way" so a cursor at e.g. 355° snaps to 0°, not 330°.
    const diff = angleDistance(angle, cand);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = cand;
    }
  }
  let usedRef: number | null = null;
  if (refRad != null) {
    for (const offset of SNAP_ANGLES_RAD) {
      let cand = (refRad + offset) % TAU;
      if (cand < 0) cand += TAU;
      const diff = angleDistance(angle, cand);
      if (diff < bestDiff && diff <= ANGLE_REFERENCE_TOLERANCE_RAD) {
        bestDiff = diff;
        best = cand;
        usedRef = refRad;
      }
    }
  }
  return {
    x: start.x + Math.cos(best) * length,
    y: start.y + Math.sin(best) * length,
    angleRad: best,
    refRad: usedRef,
  };
}

/**
 * Resolve the axis-aligned bounding box for a rect / ellipse drag, shared by
 * ShapeTool, CutoutTool, and ConstructionTool's draw sub-mode.
 *
 * - Free drag (both presets null): the box spans `start`→`current`. With
 *   `fromCenter`, `start` is the *centre* and the box grows symmetrically out
 *   to `current`, so the cursor delta becomes the half-extent on each axis.
 * - Preset drag (one or both axes locked): a locked axis takes its preset
 *   dimension; the unlocked axis follows the cursor delta (doubled under
 *   `fromCenter`, since the delta is then a half-extent). Without
 *   `fromCenter` the box anchors its top-left at `start` (legacy behaviour);
 *   with it, the box stays centred on `start`.
 *
 * The square / circle constraint is applied to `current` by the caller
 * (`constrainSquare`) *before* this runs, so an equal-magnitude delta yields a
 * centred square here without any extra handling.
 */
export function resolveDragBox(
  start: Point2,
  current: Point2,
  opts: {
    presetWidth?: number | null;
    presetHeight?: number | null;
    fromCenter?: boolean;
  } = {},
): { left: number; top: number; width: number; height: number } {
  const presetWidth = opts.presetWidth ?? null;
  const presetHeight = opts.presetHeight ?? null;
  const fromCenter = opts.fromCenter ?? false;
  const dx = Math.abs(current.x - start.x);
  const dy = Math.abs(current.y - start.y);

  if (presetWidth == null && presetHeight == null) {
    if (fromCenter) {
      return {
        left: start.x - dx,
        top: start.y - dy,
        width: dx * 2,
        height: dy * 2,
      };
    }
    return {
      left: Math.min(start.x, current.x),
      top: Math.min(start.y, current.y),
      width: dx,
      height: dy,
    };
  }

  const width = presetWidth ?? (fromCenter ? dx * 2 : dx);
  const height = presetHeight ?? (fromCenter ? dy * 2 : dy);
  if (fromCenter) {
    return {
      left: start.x - width / 2,
      top: start.y - height / 2,
      width,
      height,
    };
  }
  return { left: start.x, top: start.y, width, height };
}

/**
 * Map a two-point drag (press point → cursor) to its axis endpoints — used by
 * the slot / pill capsule and the single-segment construction line. Normally
 * the press point is one end and the shape runs from there to the cursor. With
 * `fromCenter` (Alt / centre snap), the press point is the MIDPOINT instead, so
 * the axis extends symmetrically through it — the far end mirrors the cursor
 * about the press point (issue #389). The cursor end is kept verbatim so any
 * upstream angle / length snap already applied to it survives.
 */
export function resolveDragAxis(
  start: Point2,
  end: Point2,
  fromCenter: boolean,
): { a: Point2; b: Point2 } {
  if (fromCenter) {
    return {
      a: { x: 2 * start.x - end.x, y: 2 * start.y - end.y },
      b: { x: end.x, y: end.y },
    };
  }
  return { a: { x: start.x, y: start.y }, b: { x: end.x, y: end.y } };
}

/**
 * Constrain a corner-to-corner drag (e.g., rectangle / ellipse from two
 * corners) to a square / circle by forcing equal magnitudes on both axes.
 * Uses the larger of |dx| and |dy| so the constrained shape extends to the
 * dominant axis rather than collapsing to the smaller one.
 */
export function constrainSquare(start: Point2, end: Point2): Point2 {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const size = Math.max(Math.abs(dx), Math.abs(dy));
  if (size < 1e-4) return { x: end.x, y: end.y };
  return {
    x: start.x + (dx >= 0 ? size : -size),
    y: start.y + (dy >= 0 ? size : -size),
  };
}
